import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import Toast from '../../components/Toast'
import type { EdgeConfigResult, EdgeDevice } from '../../types'
import DeviceDialog from './DeviceDialog'
import ApplySpecDialog from './ApplySpecDialog'
import TagEditor from './TagEditor'
import { useServerSpec } from './specLookup'
import { currentReading, useEdgeDevices, useLiveTagValues } from './shared'
import type { TagReading } from './shared'
import { MessageCard, ReadingTimeCell, ReadingValueCell } from './ui'

// 디바이스 탭 (Edge팀 전용): 엣지에 SignalR 로 즉시 요청한 설정 스냅샷 (EM 은 저장하지 않음) — 왼쪽 목록, 오른쪽 태그.
// 탭을 보는 동안만 엣지가 태그 값(로우 데이터)을 스트림으로 올리고, 스냅샷의 마지막 값 위에 제자리 갱신한다.
// 디바이스 추가·수정·삭제·태그 편집은 엣지 Parameter.xml 에 바로 저장된다 — 모니터링이 멈춰 있을 때만 (lockReason).
// [명세 → Edge 적용]은 디바이스 명세서의 디바이스를 골라 한꺼번에 넣는다 (ApplySpecDialog).
// 고른 명세서는 서버별로 기억해 다음에 처음 고른 값으로 쓰고, 마스터데이터 항목 코드 기본값({설비코드}_{Alias})에서
// Edge 태그 Alias 가 비었을 때 대신 쓴다
type DialogState = { kind: 'device'; code?: string } | { kind: 'tags'; device: EdgeDevice } | { kind: 'spec' } | null

export default function DevicesTab({ serverId, lockReason }: { serverId: string; lockReason: string | null }) {
  const qc = useQueryClient()
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [dialog, setDialog] = useState<DialogState>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const live = useLiveTagValues(serverId)
  const { data: devices = [], error, isFetching, isError, dataUpdatedAt, refetch } = useEdgeDevices(serverId)
  const { specId, setSpecId } = useServerSpec(serverId, false)

  const selected = devices.find(d => d.code === selectedCode) ?? devices[0]

  // 엣지에 저장한 뒤 — 디바이스 목록을 다시 받는다 (마스터데이터 설정도 같은 캐시를 쓴다)
  const onSaved = (result: EdgeConfigResult) => {
    setDialog(null)
    qc.invalidateQueries({ queryKey: ['servers', serverId, 'devices'] })
    setToast({ message: result.message, type: 'success' })
  }

  const deleteDevice = async (d: EdgeDevice) => {
    if (!confirm(`디바이스 '${d.name}' (${d.code}) 를 삭제할까요?
태그 ${d.tags.length}개와, 이 태그를 쓰던 마스터데이터 항목의 연결도 함께 사라집니다.`)) return
    try {
      const { data } = await serversApi.deleteDevice(serverId, d.code)
      setSelectedCode(null)
      onSaved(data)
    } catch (err) {
      setToast({ message: getErrorMessage(err, '삭제하지 못했습니다.'), type: 'error' })
    }
  }

  // lg 이상: 탭 컨텐츠 높이를 채우고 목록·태그 카드가 각자 스크롤 (thead 고정). 좁은 화면은 세로로 쌓아 바깥 스크롤
  return (
    <div className="lg:h-full lg:flex lg:flex-col">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="flex items-center gap-3 mb-4 shrink-0">
        <button onClick={() => setDialog({ kind: 'device' })} disabled={!!lockReason} title={lockReason ?? undefined}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-accent-hover transition disabled:opacity-40 disabled:cursor-not-allowed">
          + 디바이스 추가
        </button>
        {/* Edge 의 현재 디바이스를 받아야 추가/덮어쓰기를 가를 수 있다 — 못 받았으면 막는다. 잠겨 있어도 미리보기는 된다 */}
        <button onClick={() => setDialog({ kind: 'spec' })} disabled={isError || !dataUpdatedAt}
          title="명세서의 디바이스를 골라 이 Edge(현장) 설정에 적용합니다"
          className="border border-blue-300 text-blue-700 px-3 py-1.5 rounded-lg text-xs hover:bg-blue-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
          명세 → Edge 적용
        </button>
        {lockReason && <span className="text-xs text-gray-400">{lockReason}</span>}
        <span className="text-xs text-gray-400">
          {dataUpdatedAt ? `조회 시각 ${new Date(dataUpdatedAt).toLocaleTimeString('ko-KR', { hour12: false })} · 디바이스 ${devices.length}개` : ''}
        </span>
        <button onClick={() => refetch()} disabled={isFetching}
          className="ml-auto border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-50">
          {isFetching ? '불러오는 중…' : '새로고침'}
        </button>
      </div>

      {isError ? (
        <MessageCard tone="error">{getErrorMessage(error, '디바이스 목록을 불러오지 못했습니다.')}</MessageCard>
      ) : devices.length === 0 ? (
        <MessageCard>{isFetching ? 'Edge에서 디바이스 목록을 받는 중…' : '등록된 디바이스가 없습니다.'}</MessageCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:grid-rows-[minmax(0,1fr)] lg:flex-1 lg:min-h-0 gap-4 items-start">
          <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden flex flex-col lg:max-h-full">
            <div className="min-h-0 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3">디바이스</th>
                    <th className="text-left px-4 py-3">채널</th>
                    <th className="text-right px-4 py-3 w-16">태그</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {devices.map(d => (
                    <tr key={d.code} onClick={() => setSelectedCode(d.code)}
                      className={`cursor-pointer ${d.code === selected?.code ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-800">{d.name}</div>
                        <div className="text-gray-400 font-mono text-xs">{d.code}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{d.channelType || '-'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{d.tags.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selected && (
            <DeviceTagsPanel device={selected} live={live} lockReason={lockReason}
              onEditDevice={() => setDialog({ kind: 'device', code: selected.code })}
              onEditTags={() => setDialog({ kind: 'tags', device: selected })}
              onDelete={() => deleteDevice(selected)} />
          )}
        </div>
      )}

      {dialog?.kind === 'device' && (
        <DeviceDialog serverId={serverId} deviceCode={dialog.code} onClose={() => setDialog(null)} onDone={onSaved} />
      )}
      {dialog?.kind === 'spec' && (
        <ApplySpecDialog serverId={serverId} initialSpecId={specId} onSpecChange={setSpecId} edgeDevices={devices} lockReason={lockReason}
          onClose={() => setDialog(null)}
          onApplied={(message, failed) => {
            qc.invalidateQueries({ queryKey: ['servers', serverId, 'devices'] })
            setToast({ message, type: failed ? 'error' : 'success' })
          }} />
      )}
      {dialog?.kind === 'tags' && (
        <TagEditor serverId={serverId} device={dialog.device} onClose={() => setDialog(null)} onSaved={onSaved} />
      )}
    </div>
  )
}

function DeviceTagsPanel({ device, live, lockReason, onEditDevice, onEditTags, onDelete }: {
  device: EdgeDevice
  live: Record<string, TagReading>
  lockReason: string | null
  onEditDevice: () => void
  onEditTags: () => void
  onDelete: () => void
}) {
  const actionClass = 'text-xs text-gray-500 hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-500'
  return (
    <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden flex flex-col lg:max-h-full">
      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className="font-semibold text-gray-800">{device.name} <span className="text-gray-400 font-mono text-xs font-normal">{device.code}</span></div>
          <div className="ml-auto flex items-center gap-3" title={lockReason ?? undefined}>
            <button onClick={onEditTags} disabled={!!lockReason} className={actionClass}>태그 편집</button>
            <button onClick={onEditDevice} disabled={!!lockReason} className={actionClass}>설정</button>
            <button onClick={onDelete} disabled={!!lockReason}
              className="text-xs text-red-600/80 hover:text-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">삭제</button>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <span>채널 {device.channelType || '-'}</span>
          {device.protocol && <span>프로토콜 {device.protocol}</span>}
          {device.interval != null && <span>주기 {device.interval}ms</span>}
          {device.description && <span>{device.description}</span>}
        </div>
      </div>
      <div className="min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
            <tr>
              <th className="text-left px-4 py-3">코드</th>
              <th className="text-left px-4 py-3">값</th>
              <th className="text-left px-4 py-3">수집 시각</th>
              <th className="text-left px-4 py-3">주소</th>
              <th className="text-left px-4 py-3">타입</th>
              <th className="text-right px-4 py-3">크기</th>
              <th className="text-left px-4 py-3">연산</th>
              <th className="text-left px-4 py-3">설명</th>
              <th className="text-left px-4 py-3" title="태그 별칭 — 마스터데이터 전송코드 {설비코드}_{Alias}">Alias</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {device.tags.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">태그가 없습니다.</td></tr>
            ) : (
              device.tags.map(t => {
                const reading = currentReading(t, live)
                return (
                  <tr key={t.code} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-800">
                      {t.code}
                      {t.externalCode && <div className="text-gray-400">→ {t.externalCode}</div>}
                    </td>
                    <ReadingValueCell reading={reading} />
                    <ReadingTimeCell reading={reading} />
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{t.address || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{t.dataType}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-600">{t.size}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{t.arithmetic || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{t.description || '-'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{t.alias || '-'}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
